// loadsSummaryTableConfig.ts
// Table configuration for the Dashboard Loads Summary: column definitions, row
// coloring, and shared SQLTable props. Kept separate from the panel component
// (loadsSummary.tsx), mirroring ScheduleTable's component/tableConfig split.
import { TableColumnConfig } from '../../../components/sql_table/tableMain';
import { GroupBy, isCompleted, isCompletedPriorDay, isCanceledLoad, isRejectedLoad } from './loadsHelpers';

const groupLabel = (g: GroupBy) => (g === 'producer' ? 'Producer' : 'Carrier');

// Shared SQLTable props for the read-only summary/detail tables.
export const baseTableProps = {
    skipDataFetch: true,
    showTable: true,
    tableTools: false,
    enableFilters: false,
    downloadable: false,
    refreshable: false,
    showHeader: true,
} as const;

// Drilldown row coloring: red for Canceled/Rejected, green for done, yellow otherwise.
// Canceled/Rejected are notes-aware (isCanceledLoad/isRejectedLoad), matching the counts.
// `isDone` varies by panel: today judges completion from the notes (isCompleted), prior
// day from status-OR-notes (isCompletedPriorDay).
const makeStatusRowColor = (isDone: (r: any) => boolean) => [
    { condition: (r: any) => isCanceledLoad(r) || isRejectedLoad(r), color: '#f8d7da' },
    { condition: (r: any) => isDone(r), color: 'var(--color-green-complete, #d4edda)' },
    { condition: (r: any) => !isDone(r) && !isCanceledLoad(r) && !isRejectedLoad(r), color: 'var(--color-yellow, #fff8dc)' },
];
export const statusRowColor = makeStatusRowColor(isCompleted);
export const priorStatusRowColor = makeStatusRowColor(isCompletedPriorDay);

// Columns for the drilldown detail tables (individual loads).
export const detailCols: TableColumnConfig[] = [
    { colName: 'ticket_number', colHeader: 'Ticket #', colType: 'text', editable: false },
    { colName: 'load_date', colHeader: 'Load Date', colType: 'text', editable: false },
    { colName: 'producer_name', colHeader: 'Producer', colType: 'text', editable: false },
    { colName: 'carrier_name', colHeader: 'Carrier', colType: 'text', editable: false },
    { colName: 'destination_name', colHeader: 'Destination', colType: 'text', editable: false },
    { colName: 'gross_barrels', colHeader: 'Gross', colType: 'number', decimals: 2, editable: false },
    { colName: 'net_barrels', colHeader: 'Net', colType: 'number', decimals: 2, editable: false },
    { colName: 'bsw', colHeader: 'BSW', colType: 'number', decimals: 2, editable: false },
    { colName: 'observed_gravity', colHeader: 'Obs Grav', colType: 'number', decimals: 1, editable: false },
    { colName: 'corrected_gravity', colHeader: 'Corr Grav', colType: 'number', decimals: 1, editable: false },
    { colName: 'status', colHeader: 'Status', colType: 'text', editable: false },
    { colName: 'status_notes', colHeader: 'Status Notes', colType: 'text', editable: false },
];

// Columns for the today/tomorrow completed-vs-outstanding summary (grouped by producer/carrier).
export const getDaySummaryCols = (groupBy: GroupBy): TableColumnConfig[] => [
    { colName: 'group', colHeader: groupLabel(groupBy), colType: 'text', editable: false },
    { colName: 'totalLoads', colHeader: 'Total Loads', colType: 'number', editable: false },
    { colName: 'completed', colHeader: 'Completed', colType: 'number', editable: false },
    { colName: 'outstanding', colHeader: 'Outstanding', colType: 'number', editable: false },
    { colName: 'canceledLoads', colHeader: 'Canceled', colType: 'number', editable: false },
    { colName: 'rejectedLoads', colHeader: 'Rejected', colType: 'number', editable: false },
    { colName: 'wasteLoads', colHeader: 'Waste Loads', colType: 'number', editable: false },
];

// Columns for the prior-day stats summary (grouped by producer/carrier).
export const getPriorSummaryCols = (groupBy: GroupBy): TableColumnConfig[] => [
    { colName: 'group', colHeader: groupLabel(groupBy), colType: 'text', editable: false },
    { colName: 'totalLoads', colHeader: 'Total Loads', colType: 'number', decimals: 0, editable: false },
    { colName: 'completedLoads', colHeader: 'Completed', colType: 'number', decimals: 0, editable: false },
    { colName: 'totalVolume', colHeader: 'Total Volume (bbl)', colType: 'number', decimals: 2, editable: false },
    { colName: 'avgBsw', colHeader: 'Avg BSW', colType: 'number', decimals: 2, editable: false },
    { colName: 'incompleteLoads', colHeader: 'Uncompleted', colType: 'number', editable: false },
    { colName: 'canceledLoads', colHeader: 'Canceled', colType: 'number', decimals: 0, editable: false },
    { colName: 'rejectedLoads', colHeader: 'Rejected', colType: 'number', decimals: 0, editable: false },
];
